/**
 * Version API client for PWA version handshake
 * Uses direct fetch without auth - version endpoint is public
 */

import { env } from '@/config/env'

/**
 * Response from the /api/version endpoint
 */
export interface VersionResponse {
    frontendMinVersion: string
    timestamp: string
}

/**
 * Fetch version info from the backend
 * No authentication required, short timeout, cache bypassed
 *
 * @returns Version information or null if request fails
 */
export async function getVersion(): Promise<VersionResponse | null> {
    try {
        const controller = new AbortController()
        const timeoutId = setTimeout(() => controller.abort(), 5000) // 5 second timeout

        const response = await fetch(`${env.API_URL}/api/version`, {
            method: 'GET',
            headers: {
                'Cache-Control': 'no-cache',
            },
            signal: controller.signal,
        })

        clearTimeout(timeoutId)

        if (!response.ok) {
            console.warn('[Version Check] Failed to fetch version:', response.status)
            return null
        }

        return (await response.json()) as VersionResponse
    } catch (error) {
        // Don't throw on version check failures - just log and return null
        // This prevents version check failures from breaking the app
        if (error instanceof Error && error.name === 'AbortError') {
            console.warn('[Version Check] Request timed out')
        } else {
            console.warn('[Version Check] Error fetching version:', error)
        }
        return null
    }
}

/**
 * Compare two version strings
 * Version format: YYYY.MM.DD.HHMM (e.g., "2024.12.04.1030")
 *
 * @returns true if currentVersion is older than minVersion
 */
export function isVersionOutdated(
    currentVersion: string,
    minVersion: string
): boolean {
    // Simple string comparison works for our version format
    // because YYYY.MM.DD.HHMM is naturally sortable
    return currentVersion < minVersion
}

export const versionApi = {
    getVersion,
    isVersionOutdated,
}

